// Dynamic OAuth Redirect URI Utility
// Automatically detects the current port and constructs the correct redirect URI

/**
 * Get the current origin (protocol + host + port)
 * @returns {string} Current origin
 */
export function getCurrentOrigin() {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    console.warn('getCurrentOrigin: window is not available (SSR or server-side)');
    return 'http://localhost:5173'; // Default fallback
  }
  return window.location.origin;
}

/**
 * Get the dynamic redirect URI for OAuth
 * @param {string} path - OAuth callback path (default: '/auth/google/callback')
 * @returns {string} Complete redirect URI
 */
export function getDynamicRedirectUri(path = '/auth/google/callback') {
  const origin = getCurrentOrigin();
  return `${origin}${path}`;
}

/**
 * Check if the current redirect URI matches the configured one
 * @param {string} configuredUri - The configured redirect URI from env vars
 * @returns {boolean} Whether they match
 */
export function isRedirectUriMatching(configuredUri) {
  const currentUri = getDynamicRedirectUri();
  return currentUri === configuredUri;
}

/**
 * Get OAuth configuration with dynamic redirect URI
 * @returns {Object} OAuth configuration
 */
export function getOAuthConfig() {
  const clientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;
  const clientSecret = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_SECRET;
  const configuredRedirectUri = import.meta.env.VITE_GOOGLE_OAUTH_REDIRECT_URI;
  const scopes = (import.meta.env.VITE_GOOGLE_OAUTH_SCOPES || '').split(',');
  
  // Use dynamic redirect URI instead of configured one
  // Only use dynamic URI if we're in a browser environment
  const redirectUri = typeof window !== 'undefined' ? getDynamicRedirectUri() : configuredRedirectUri;
  const isDynamic = redirectUri !== configuredRedirectUri && typeof window !== 'undefined';
  
  // Validate redirect URI in production/staging environments
  if (typeof window !== 'undefined' && isDynamic) {
    const isProduction = import.meta.env.VITE_APP_ENV === 'production' || import.meta.env.VITE_APP_ENV === 'staging';
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (isProduction && !isLocalhost && !configuredRedirectUri) {
      console.warn('⚠️ PRODUCTION WARNING: Using dynamic redirect URI without VITE_GOOGLE_OAUTH_REDIRECT_URI set');
      console.warn('  This may cause OAuth failures if the redirect URI is not added to Google Cloud Console.');
      console.warn('  Current redirect URI:', redirectUri);
      console.warn('  Action: Set VITE_GOOGLE_OAUTH_REDIRECT_URI in your environment variables, or');
      console.warn('  Action: Add this redirect URI to Google Cloud Console:');
      console.warn(`     ${redirectUri}`);
    }
  }
  
  return {
    clientId,
    clientSecret,
    redirectUri,
    configuredRedirectUri,
    scopes: scopes.filter(scope => scope.trim()),
    isDynamic
  };
}

/**
 * Log OAuth configuration for debugging
 */
export function logOAuthConfig() {
  const config = getOAuthConfig();
  
  console.log('🔧 OAuth Configuration:');
  console.log('  Client ID:', config.clientId ? `${config.clientId.substring(0, 20)}...` : 'NOT SET');
  console.log('  Client Secret:', config.clientSecret ? 'SET' : 'NOT SET');
  console.log('  Dynamic Redirect URI:', config.redirectUri);
  console.log('  Configured Redirect URI:', config.configuredRedirectUri || 'NOT SET');
  console.log('  Using Dynamic URI:', config.isDynamic);
  console.log('  Scopes:', config.scopes);
  
  if (config.isDynamic) {
    const isProduction = typeof window !== 'undefined' && 
      (import.meta.env.VITE_APP_ENV === 'production' || import.meta.env.VITE_APP_ENV === 'staging');
    const isLocalhost = typeof window !== 'undefined' && 
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    
    if (isProduction && !isLocalhost) {
      console.warn('⚠️ PRODUCTION: Using dynamic redirect URI');
      console.warn('  Make sure this exact redirect URI is added to Google Cloud Console:');
      console.warn(`     ${config.redirectUri}`);
      console.warn('  Go to: https://console.cloud.google.com/apis/credentials');
      console.warn('  → Select your OAuth 2.0 Client ID');
      console.warn('  → Add to "Authorized redirect URIs"');
    } else {
      console.warn('⚠️ Using dynamic redirect URI. Make sure this is added to Google Cloud Console:');
      console.warn(`   ${config.redirectUri}`);
    }
  } else if (config.configuredRedirectUri) {
    console.log('✅ Using configured redirect URI:', config.configuredRedirectUri);
    console.log('  Make sure this matches what is configured in Google Cloud Console');
  }
}
